"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { ArrowRight, Mail, MessageCircle, Phone } from "lucide-react";

// Social icons (removed in lucide-react v1)
type IconProps = { size?: number; className?: string };
const FacebookIcon = ({ size = 18, className }: IconProps) => (
	<svg
		aria-hidden="true"
		focusable="false"
		xmlns="http://www.w3.org/2000/svg"
		width={size}
		height={size}
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
		strokeLinecap="round"
		strokeLinejoin="round"
		className={className}
	>
		<path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
	</svg>
);
const XIcon = ({ size = 18, className }: IconProps) => (
	<svg
		aria-hidden="true"
		focusable="false"
		xmlns="http://www.w3.org/2000/svg"
		width={size}
		height={size}
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
		strokeLinecap="round"
		strokeLinejoin="round"
		className={className}
	>
		<path d="M4 4l11.733 16h4.267l-11.733 -16h-4.267z" />
		<path d="M4 20l6.768 -6.768m2.46 -2.46l6.772 -6.772" />
	</svg>
);
const LinkedinIcon = ({ size = 18, className }: IconProps) => (
	<svg
		aria-hidden="true"
		focusable="false"
		xmlns="http://www.w3.org/2000/svg"
		width={size}
		height={size}
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
		strokeLinecap="round"
		strokeLinejoin="round"
		className={className}
	>
		<path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" />
		<rect width="4" height="12" x="2" y="9" />
		<circle cx="4" cy="4" r="2" />
	</svg>
);
const InstagramIcon = ({ size = 18, className }: IconProps) => (
	<svg
		aria-hidden="true"
		focusable="false"
		xmlns="http://www.w3.org/2000/svg"
		width={size}
		height={size}
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
		strokeLinecap="round"
		strokeLinejoin="round"
		className={className}
	>
		<rect width="20" height="20" x="2" y="2" rx="5" ry="5" />
		<path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
		<line x1="17.5" x2="17.51" y1="6.5" y2="6.5" />
	</svg>
);

import Link from "next/link";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { type FormSchemaType, formSchema } from "@/lib/form_schema";
import { submitForm } from "@/lib/form_submit";
import { buildExpertWhatsAppHref } from "@/lib/pricing-region";

const FOOTER_COPY = {
	description:
		"Crafting digital ascent for creators who dare to dream big. We run the campaigns - you create the content.",
	contact: {
		heading: "Contact",
	},
	links: {
		heading: "Explore",
		about: "About Us",
		blog: "Blog",
		privacy: "Privacy Policy",
		terms: "Terms & Conditions",
		sitemap: "Sitemap",
	},
	newsletter: {
		heading: "Join the Movement",
		description:
			"Get exclusive growth strategies and insights delivered straight to your inbox. No fluff, just results.",
		placeholder: "Enter your email address",
	},
	securedPayments: "Secured Payments",
	copyright: "TheHighraysYT Digital. All rights reserved.",
};

const FOOTER_WHATSAPP_HREF = buildExpertWhatsAppHref("usd");

const socialLinks = [
	{
		icon: FacebookIcon,
		href: "https://facebook.com/thehighrays",
		name: "facebook",
	},
	{ icon: XIcon, href: "https://twitter.com/thehighrays", name: "x" },
	{
		icon: LinkedinIcon,
		href: "https://linkedin.com/company/thehighrays",
		name: "linkedin",
	},
	{
		icon: InstagramIcon,
		href: "https://instagram.com/thehighrays",
		name: "instagram",
	},
];

const quickLinks = [
	{ label: FOOTER_COPY.links.about, href: "/about" },
	{ label: FOOTER_COPY.links.blog, href: "/blog" },
];

export default function Footer() {
	const [isSubmitting, setIsSubmitting] = useState(false);

	const {
		register,
		handleSubmit,
		reset,
		formState: { errors },
	} = useForm<FormSchemaType>({
		resolver: zodResolver(formSchema),
		defaultValues: {
			Email: "",
			Message: "Newsletter subscription from footer",
		},
	});

	const onSubmit = async (data: FormSchemaType) => {
		setIsSubmitting(true);
		try {
			// Note: Make sure submitForm handles this partial data or adjust schema if needed.
			// Assuming submitForm works with the schema provided.
			const result = await submitForm(data);

			if (result.success) {
				toast.success("Subscribed successfully! Welcome to the inner circle.");
				reset();
			} else {
				toast.error(result.error || "Subscription failed. Please try again.");
			}
		} catch (error) {
			toast.error("An error occurred. Please try again later.");
			console.error(error);
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<footer className="relative bg-[#fcfbf9] text-zinc-900 border-t border-zinc-200 overflow-hidden font-serif selection:bg-[#ef4444] selection:text-white">
			{/* Texture Background */}
			<div className="absolute inset-0 opacity-40 pointer-events-none bg-[url('https://www.transparenttextures.com/patterns/rice-paper.png')]" />

			{/* Decorative Blur */}
			<div className="absolute bottom-0 left-[-20%] w-[600px] h-[600px] bg-red-500/5 rounded-full blur-[120px] pointer-events-none" />

			<div className="container mx-auto px-6 pt-20 pb-28 relative z-10">
				<div className="grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-24">
					{/* Brand & Description (4 cols) */}
					<div className="lg:col-span-4 space-y-8">
						<div className="flex items-center group cursor-default">
							<span className="font-syne text-2xl font-bold uppercase tracking-tight">
								TheHighraysYT
							</span>
						</div>

						<p className="text-zinc-500 italic leading-relaxed text-lg font-light">
							{FOOTER_COPY.description}
						</p>

						<div className="flex gap-4">
							{socialLinks.map((social) => (
								<a
									key={social.name}
									href={social.href}
									aria-label={`Visit TheHighraysYT on ${social.name}`}
									className="w-10 h-10 rounded-full border border-zinc-200 flex items-center justify-center text-zinc-400 hover:text-white hover:bg-[#ef4444] hover:border-[#ef4444] transition-colors duration-300 group"
								>
									<social.icon
										size={18}
										className="group-hover:scale-110 transition-transform"
									/>
								</a>
							))}
						</div>
					</div>

					{/* Quick Links (3 cols) */}
					<div className="lg:col-span-3 lg:pl-8 space-y-8">
						<h3
							className="text-xl font-bold tracking-tight"
							style={{ fontFamily: "'Playfair Display', serif" }}
						>
							{FOOTER_COPY.links.heading}
						</h3>
						<ul className="space-y-4">
							{quickLinks.map((link) => (
								<li key={link.href}>
									<Link
										href={link.href}
										className="text-zinc-500 hover:text-[#ef4444] transition-colors duration-300 flex items-center gap-2 group"
									>
										<span className="w-0 group-hover:w-2 h-px bg-[#ef4444] transition-[width] duration-300" />
										{link.label}
									</Link>
								</li>
							))}
						</ul>

						<div className="pt-8">
							<h3
								className="text-xl font-bold tracking-tight mb-6"
								style={{ fontFamily: "'Playfair Display', serif" }}
							>
								{FOOTER_COPY.contact.heading}
							</h3>
							<div className="space-y-4 text-zinc-500">
								<div className="flex flex-col gap-2">
									<a
										href={FOOTER_WHATSAPP_HREF}
										target="_blank"
										rel="noreferrer"
										className="flex min-h-11 items-center gap-3 hover:text-[#ef4444] transition-colors"
										aria-label="WhatsApp TheHighraysYT"
									>
										<MessageCircle size={16} />
										<span className="animate-shine-red">WA: +1 4167264766</span>
									</a>
									<a
										href="tel:+918273262260"
										className="flex min-h-11 items-center gap-3 hover:text-[#ef4444] transition-colors"
									>
										<Phone size={16} />
										<span>+91 8273262260 (India)</span>
									</a>
								</div>
								<a
									href="mailto:info@thehighrays.com"
									className="flex items-center gap-3 hover:text-[#ef4444] transition-colors"
								>
									<Mail size={16} />
									<span>info@thehighrays.com</span>
								</a>
							</div>
						</div>
					</div>

					{/* Newsletter (5 cols) */}
					<div className="lg:col-span-5 space-y-8">
						<h3
							className="text-xl font-bold tracking-tight"
							style={{ fontFamily: "'Playfair Display', serif" }}
						>
							{FOOTER_COPY.newsletter.heading}
						</h3>
						<p className="text-zinc-500 font-light">
							{FOOTER_COPY.newsletter.description}
						</p>

						<form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
							<div className="relative">
								<Input
									type="email"
									placeholder={FOOTER_COPY.newsletter.placeholder}
									className="w-full bg-white border border-zinc-300 h-14 pl-6 pr-14 rounded-full text-zinc-900 placeholder:text-zinc-400 focus:ring-2 focus:ring-red-600/20 focus:border-[#ef4444] transition-[border-color,box-shadow] shadow-md hover:shadow-lg"
									{...register("Email")}
								/>
								<Button
									type="submit"
									disabled={isSubmitting}
									className="absolute right-1.5 top-1.5 h-11 w-11 rounded-full bg-[#ef4444] hover:bg-red-700 text-white p-0 flex items-center justify-center transition-transform hover:scale-105"
								>
									{isSubmitting ? (
										<div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
									) : (
										<ArrowRight size={18} />
									)}
								</Button>
							</div>
							{errors.Email && (
								<p className="text-[#ef4444] text-xs pl-4">
									{errors.Email.message}
								</p>
							)}
						</form>

						<div className="pt-8 w-full">
							<p className="text-zinc-400 text-xs uppercase tracking-widest mb-4 font-sans">
								{FOOTER_COPY.securedPayments}
							</p>
							<PaymentLogos />
						</div>
					</div>
				</div>

				{/* Footer Bottom */}
				<div className="mt-20 pt-8 border-t border-zinc-200/60 flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-zinc-400 font-sans">
					<div className="flex items-center gap-4">
						<p suppressHydrationWarning>
							© {new Date().getFullYear()} {FOOTER_COPY.copyright}
						</p>
					</div>
					<div className="flex flex-wrap justify-center md:justify-end gap-6">
						<Link
							href="/privacy-policy"
							className="hover:text-[#ef4444] transition-colors"
						>
							{FOOTER_COPY.links.privacy}
						</Link>
						<Link
							href="/terms-and-conditions"
							className="hover:text-[#ef4444] transition-colors"
						>
							{FOOTER_COPY.links.terms}
						</Link>
						<a
							href="/sitemap.xml"
							className="hover:text-[#ef4444] transition-colors"
						>
							{FOOTER_COPY.links.sitemap}
						</a>
					</div>
				</div>
			</div>
		</footer>
	);
}

const paymentMethods = [
	{ name: "UPI", logo: "/logos/upi.svg", width: 40, height: 24, bg: "white" },
	{
		name: "PayPal",
		logo: "/logos/PayPal.png",
		width: 50,
		height: 24,
		bg: "white",
	},
	{
		name: "Visa",
		logo: "/logos/Visa_Inc.png",
		width: 50,
		height: 24,
		bg: "white",
	},
	{
		name: "Mastercard",
		logo: "/logos/Mastercard-logo.png",
		width: 40,
		height: 24,
		bg: "white",
	},
	{
		name: "Amex",
		logo: "/logos/American_Express.png",
		width: 50,
		height: 24,
		bg: "#026fd1",
	}, // Keep distinct bg for amex if needed or make uniform
];

const PaymentLogos = () => {
	return (
		<div className="flex flex-wrap gap-3">
			{paymentMethods.map((method) => (
				<div
					key={method.name}
					className="h-10 px-3 flex items-center justify-center bg-white border border-zinc-200 rounded-lg grayscale opacity-60 hover:grayscale-0 hover:opacity-100 transition-[filter,opacity,box-shadow] duration-300 hover:shadow-md"
				>
					<div className="relative h-6 w-auto min-w-[30px] flex items-center justify-center">
						{/* Using standard img for simplicity with external assets or Next Image with specific sizing */}
						{/* biome-ignore lint/performance/noImgElement: Small payment logos do not need Next image optimization and must preserve native aspect ratios. */}
						<img
							src={method.logo}
							alt={method.name}
							width={method.width}
							height={method.height}
							className="object-contain"
							style={{ maxHeight: "100%" }}
						/>
					</div>
				</div>
			))}
		</div>
	);
};
